import classNames from 'classnames'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { v4 } from 'uuid'

import Icon from '@/components/Icon/Icon'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import { useAppSelector } from '@/store/hooks'
import { PriceMapType } from '@/types/card'
import { CardType } from '@/types/card'
import { impactLink } from '@/utils/helpers'

import styles from './CardPrices.module.scss'

type TCGPContainerProps = {
  children: any
  saleEndTime: string
  tcgplayerSale: number
}

const TCGPContainer = ({ children, saleEndTime, tcgplayerSale }: TCGPContainerProps) =>
  tcgplayerSale > 0 ? (
    <OverlayTrigger
      overlay={
        <Tooltip id='button-tooltip'>
          <div>On Sale Until</div>
          <div>{saleEndTime}</div>
        </Tooltip>
      }
      placement='top'
    >
      {children}
    </OverlayTrigger>
  ) : (
    children
  )

type Props = {
  card: CardType
  scalable?: boolean
}

const CardPrices = ({ card, scalable }: Props) => {
  const { tcgplayerKickback, tcgplayerSale } = useAppSelector((state) => state.misc)
  const priceSources = useAppSelector((state) => state.user.priceSources)

  const { names, prices } = card

  const scgSlug = encodeURIComponent(
    (names ? names.join(' // ') : '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace('꞉', ':'), // Ratonhnhaké꞉ton uses a "colon sign" which is NOT a colon
  ).replace(/\s/g, '%20')

  const priceLinks: Partial<Record<keyof PriceMapType, () => React.ReactNode>> = {
    cardhoarder: () => (
      <OutboundLink key={v4()} title='Buy at Cardhoarder' url={prices.cardhoarder.url}>
        <div className={styles.price}>
          <span>{`${prices.cardhoarder.price} TIX`}</span>
        </div>
      </OutboundLink>
    ),
    cardkingdom: () => (
      <OutboundLink
        key={v4()}
        title='Buy at Card Kingdom'
        url={`${prices.cardkingdom.url}&partner_args=edhrec,card`}
        utm_medium='affiliate'
      >
        <div className={styles.price}>
          <Icon icon='cardkingdom' />
          <span>{`$${prices.cardkingdom.price.toFixed(2)}`}</span>
        </div>
      </OutboundLink>
    ),
    cardmarket: () => (
      <OutboundLink
        key={v4()}
        title='Buy at Cardmarket'
        url={`${prices.cardmarket.url}&utm_source=edhrec&utm_medium=text&utm_campaign=card_prices`}
        utm_medium='text'
      >
        <div className={styles.price}>
          <Icon icon='cardmarket' />
          <span>
            {`${
              typeof prices.cardmarket.price === 'number' ? prices.cardmarket.price.toFixed(2) : prices.cardmarket.price
            } €`.replace('.', ',')}
          </span>
        </div>
      </OutboundLink>
    ),
    face2face: () => {
      const fixedName = card.names.length > 1 ? card.names.join(' // ') : card.name
      const url = `https://www.facetofacegames.com/search?q=${fixedName}&dt_id=2364253`
      return (
        <OutboundLink key={v4()} title='Buy at Face to Face Games' url={url} utm_medium='affiliate'>
          <div className={styles.price}>
            <Icon icon='face2face' />
            <span>${prices.face2face.price.toFixed(2)}</span>
          </div>
        </OutboundLink>
      )
    },
    /* mtgstocks: () => (
      <OutboundLink
        key={v4()}
        title='View on MTGStocks'
        url={prices.mtgstocks.url}
      >
        <div className={styles.price}>
          <span>📈${prices.mtgstocks.price.toFixed(2)}</span>
        </div>
      </OutboundLink>
    ), */
    manapool: () => (
      <OutboundLink
        key={v4()}
        title='Buy at Mana Pool'
        url={`https://manapool.com/card/${prices.manapool.slug}?ref=scm&ref_meta=referrer:edhrec,cardprice`}
        utm_medium='affiliate'
      >
        <div className={styles.price}>
          <Icon icon='manapool' />
          <span>${prices.manapool.price.toFixed(2)}</span>
        </div>
      </OutboundLink>
    ),
    scg: () => (
      <OutboundLink
        disableUtm
        key={v4()}
        title='Buy at Star City Games'
        url={impactLink({
          partner: 'scg',
          subId1: 'edhrec,cardprice',
          u: `https://starcitygames.com/search/?item_display_name=${scgSlug}`,
        })}
      >
        <div className={styles.price}>
          <Icon icon='scg' />
          <span>${prices.scg.price}</span>
        </div>
      </OutboundLink>
    ),
    tcgl: () => (
      <OutboundLink
        key={v4()}
        title='Buy at TCG Land'
        url={`https://www.tcg.land/product/mtg_single_${prices.tcgl.slug}?via=cardshops-edhrec-com`}
        utm_medium='affiliate'
      >
        <div className={styles.price}>
          <Icon icon='tcgl' />
          <span>${prices.tcgl.price} MXN</span>
        </div>
      </OutboundLink>
    ),
    tcgplayer: () => {
      const end = tcgplayerKickback ? new Date(tcgplayerKickback.end) : new Date()
      const saleEndTime = `${end.getHours() % 12}:${end.getMinutes().toString().padStart(2, '0')} ${
        end.getHours() > 11 ? 'P' : 'A'
      }M ${end.toLocaleDateString()}`
      return (
        <TCGPContainer key={v4()} saleEndTime={saleEndTime} tcgplayerSale={tcgplayerSale}>
          <a
            href={impactLink({
              partner: 'tcgplayer',
              subId1: 'edhrec,cardprice',
              u: prices.tcgplayer.url.split('?')[0],
            })}
            rel='noopener noreferrer'
            target='_blank'
          >
            <div className={classNames(styles.price, { [styles.sale]: tcgplayerSale > 0 })}>
              <Icon icon='tcgplayer' />
              <span>{`$${(((100 - tcgplayerSale) / 100) * prices.tcgplayer.price).toFixed(2)}`}</span>
            </div>
          </a>
        </TCGPContainer>
      )
    },
  }
  return (
    <div className={classNames(styles.prices, scalable && styles.scalable)}>
      {/* priceSources should always be defined, but a user/browser could mess it up */}
      {prices && priceSources && (
        <>
          {[0, 1, 2].map((i) => {
            let priceSource = priceSources[i]

            // Fix users with CSI in their settings, which was removed
            if (priceSource === ('coolstuffinc' as any)) priceSource = 'scg'

            return prices[priceSource] && priceLinks[priceSource] ? (
              (priceLinks[priceSource] as any)()
            ) : (
              <span key={v4()}>&nbsp;</span>
            )
          })}
        </>
      )}
    </div>
  )
}

export default CardPrices
